/**
 * Application shell — Mantine `AppShell` with a navbar and a header.
 * Wraps every authenticated route. `/login` is rendered outside this shell.
 *
 * **The header carries the breadcrumb, the search box, and the per-viewer
 * controls** — theme, Settings, notifications. A strip of chrome across every
 * page, permanently, has to earn its height, so nothing is duplicated up here:
 * each of those controls lives in the header and nowhere else, because a second
 * copy is a control that can disagree with the original.
 *
 * The **navigation toggle** is the exception that stays in the navbar. It has to
 * travel with the thing it collapses, and the rail is never fully hidden, so it
 * is always reachable where it is.
 *
 * The navbar never disappears — it collapses to a narrow icon rail, on every
 * screen size. That is what keeps the header free of a burger: the control that
 * reopens the navigation is always on screen.
 *
 * A page's code is loaded when it is first opened (ADO 2544); meanwhile the
 * shell stays and the content area shows a loader.
 */

import { Suspense, type PropsWithChildren } from "react";
import { AppShell, Center, Loader } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useLocation } from "react-router-dom";
import { HeaderPanel } from "./HeaderPanel";
import { NavbarPanel } from "./NavbarPanel";

/** Width of the icon-only rail. Fits an 18px icon plus its hit area. */
const RAIL_WIDTH = 64;
const FULL_WIDTH = 264;

export function AppShellLayout({ children }: PropsWithChildren) {
  const { pathname } = useLocation();
  const area = pathname.startsWith("/validation") ? "validation" : undefined;
  const [opened, { toggle }] = useDisclosure(true);

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{
        width: opened ? FULL_WIDTH : RAIL_WIDTH,
        breakpoint: "sm",
        // Never fully collapsed, on any size: the control that brings the
        // navigation back lives inside it.
        collapsed: { mobile: false, desktop: false },
      }}
      padding="md"
    >
      <AppShell.Header>
        <HeaderPanel />
      </AppShell.Header>
      <AppShell.Navbar>
        <NavbarPanel collapsed={!opened} onToggle={toggle} />
      </AppShell.Navbar>
      {/* Marks the validation area so its boxes read as cards (brand.css). */}
      <AppShell.Main data-area={area}>
        <Suspense
          fallback={
            <Center py="xl">
              <Loader size="sm" aria-label="Loading page" />
            </Center>
          }
        >
          {children}
        </Suspense>
      </AppShell.Main>
    </AppShell>
  );
}
